import axios from 'axios';
import { defineStore } from 'pinia';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsPlayer } from '../softswiss/player';
import type {
  IHttpRequestOptions,
  IHttpResponse,
  IHttpResponsesCollection,
} from '~/types/transport/http';

interface TransportCachebleDataInterface {
  requestId: string;
  response: IHttpResponse;
}

export const softswiss = defineStore('transportSoftswis', {
  state: () => ({
    data: {} as IHttpResponsesCollection,
  }),
  actions: {
    SAVE_DATA(data: IHttpResponsesCollection) {
      this.data = { ...this.data, ...data };
    },

    SAVE_DATA_CACHEBLE(data: TransportCachebleDataInterface) {
      this.data[data.requestId] = data.response;
      _saveToLocalStorage(data);
    },

    async get<T = any>(requestOptions: IHttpRequestOptions): Promise<T | null> {
      if (!import.meta.browser || !window) {
        console.error('REQUESTS TO SS MUST BE ONLY FROM BROWSER!!!!!');
      }

      const storeRoot = useStoreRoot();

      for (let tryCount = 0; tryCount < 5; tryCount++) {
        try {
          const host = location.host || 'www.7bitcasino.com';

          if (storeRoot.env !== 'prod') {
            console.info('request to SS via host', host, requestOptions.path);
          }

          const cacheTime = requestOptions.updateInterval || storeRoot.updateInterval;

          const method = 'GET';

          const requestId = `${method}@${host}${requestOptions.path}`;

          if (
            !this.data[requestId] &&
            !requestOptions.force &&
            requestOptions.cacheble &&
            import.meta.browser &&
            localStorage &&
            localStorage[requestId] &&
            Date.now() < Number(localStorage[requestId + '_timestamp']) + cacheTime
          ) {
            const left = Math.floor(
              (Number(localStorage[requestId + '_timestamp']) + cacheTime - Date.now()) / 1000 / 60
            );
            const hours = Math.floor(left / 60);
            const minutes = left - hours * 60;
            console.info(
              `${requestId} is restored from localStroage, and will be updated only after ${hours} hours ${minutes} minutes, when cache expires!`
            );

            this.SAVE_DATA({
              [requestId]: {
                response: Promise.resolve(JSON.parse(localStorage[requestId])),
                requestTimestamp: Number(localStorage[requestId + '_timestamp']),
              },
            });
          } else if (
            !this.data[requestId] ||
            requestOptions.force ||
            Date.now() > this.data[requestId].requestTimestamp + cacheTime
          ) {
            const axiosRequest = async () => {
              try {
                const response = await axios.get(requestOptions.path, {
                  withCredentials: true,
                  params: requestOptions.params,
                  timeout: 60000,
                  headers: {
                    Accept: 'application/vnd.s.v1+json',
                    'Content-Type': 'application/json',
                    'accept-language': storeRoot.userLang,
                  },
                });

                return response.data;
              } catch (e: any) {
                if (requestOptions.getErrors && e?.response?.data) {
                  return { errors: e?.response?.data };
                }
                if (e.response.status === 401) {
                  await useStoreSsPlayer().deletePlayerInfoFromLocalStorage();
                  window.location.reload();
                }
                return null;
              }
            };

            const response = import.meta.browser ? axiosRequest() : await axiosRequest();

            this.SAVE_DATA({
              [requestId]: {
                response,
                requestTimestamp: Date.now(),
              },
            });
          }

          return this.data[requestId].response || null;
        } catch (e: any) {
          console.error(e);
        }
      }
      console.error(new Error('SOFTSWISS CONNECTION ERROR'));
      return null;
    },

    async post<T = any>(requestOptions: IHttpRequestOptions): Promise<T | null> {
      if (!import.meta.browser || !window) {
        console.error('REQUESTS TO SS MUST BE ONLY FROM BROWSER!!!!!');
      }

      const storeRoot = useStoreRoot();

      for (let tryCount = 0; tryCount < 5; tryCount++) {
        try {
          const host = location.host || 'www.7bitcasino.com';

          if (storeRoot.env !== 'prod') {
            console.info('request to SS via host', host, requestOptions.path);
          }

          const cacheTime = requestOptions.updateInterval || storeRoot.updateInterval;

          const method = 'POST';

          const requestId = `${method}@${host}${requestOptions.path}`;

          if (requestOptions.force !== false) {
            requestOptions.force = true;
          }

          if (
            !this.data[requestId] ||
            requestOptions.force ||
            Date.now() > this.data[requestId].requestTimestamp + cacheTime
          ) {
            const axiosRequest = async () => {
              try {
                const response = await axios.post(requestOptions.path, requestOptions.payload, {
                  withCredentials: true,
                  timeout: 60000,
                  headers: {
                    Accept: 'application/vnd.s.v1+json',
                    'Content-Type': 'application/json',
                    'accept-language': storeRoot.userLang,
                    ...(requestOptions.headers || {}),
                  },
                });

                return response.data;
              } catch (e: any) {
                if (requestOptions.getErrors && e?.response?.data?.errors) {
                  return { errors: e?.response?.data?.errors };
                }

                if (requestOptions.getRequired && e?.response?.data?.required) {
                  return { required: e?.response?.data?.required };
                }

                console.error(e);
                return null;
              }
            };

            const response = import.meta.browser ? axiosRequest() : await axiosRequest();

            this.SAVE_DATA({
              [requestId]: {
                response,
                requestTimestamp: Date.now(),
              },
            });
          }
          return this.data[requestId].response || null;
        } catch (e: any) {
          console.error(e);
        }
      }
      console.error(new Error('SOFTSWISS CONNECTION ERROR'));
      return null;
    },

    async patch<T = any>(requestOptions: IHttpRequestOptions): Promise<T | null> {
      if (!import.meta.browser || !window) {
        console.error('REQUESTS TO SS MUST BE ONLY FROM BROWSER!!!!!');
      }

      const storeRoot = useStoreRoot();

      for (let tryCount = 0; tryCount < 5; tryCount++) {
        try {
          const axiosRequest = async () => {
            try {
              const response = await axios.patch(requestOptions.path, requestOptions.payload, {
                withCredentials: true,
                timeout: 60000,
                headers: {
                  Accept: 'application/vnd.s.v1+json',
                  'Content-Type': 'application/json',
                  'accept-language': storeRoot.userLang,
                },
              });

              return response.data;
            } catch (e: any) {
              if (requestOptions.getErrors && e?.response?.data?.errors) {
                return { errors: e?.response?.data?.errors };
              }

              console.error(e);
              return null;
            }
          };

          // Transfer state cant send Promise to browser,
          // thats why we must resolve promise for before transfer
          const response = import.meta.browser ? axiosRequest() : await axiosRequest();

          return response || null;
        } catch (e: any) {
          console.error(e);
        }
      }
      console.error(new Error('SOFTSWISS CONNECTION ERROR'));
      return null;
    },

    async put<T = any>(requestOptions: IHttpRequestOptions): Promise<T | null> {
      if (!import.meta.browser || !window) {
        console.error('REQUESTS TO SS MUST BE ONLY FROM BROWSER!!!!!');
      }

      const storeRoot = useStoreRoot();

      for (let tryCount = 0; tryCount < 5; tryCount++) {
        try {
          const axiosRequest = async () => {
            try {
              const response = await axios.put(requestOptions.path, requestOptions.payload, {
                withCredentials: true,
                timeout: 60000,
                headers: {
                  Accept: 'application/vnd.s.v1+json',
                  'Content-Type': 'application/json',
                  'accept-language': storeRoot.userLang,
                },
              });

              return response.data;
            } catch (e: any) {
              if (requestOptions.getErrors && e?.response?.data?.errors) {
                return { errors: e?.response?.data?.errors };
              }

              console.error(e);
              return null;
            }
          };

          // Transfer state cant send Promise to browser,
          // thats why we must resolve promise for before transfer
          const response = import.meta.browser ? axiosRequest() : await axiosRequest();

          return response || null;
        } catch (e: any) {
          console.error(e);
        }
      }
      console.error(new Error('SOFTSWISS CONNECTION ERROR'));
      return null;
    },

    async delete<T = any>(requestOptions: IHttpRequestOptions): Promise<T | any | null> {
      if (!import.meta.browser || !window) {
        console.error('REQUESTS TO SS MUST BE ONLY FROM BROWSER!!!!!');
      }

      const storeRoot = useStoreRoot();

      for (let tryCount = 0; tryCount < 5; tryCount++) {
        try {
          const axiosRequest = async () => {
            try {
              const response = await axios.delete(requestOptions.path, {
                withCredentials: true,
                timeout: 60000,
                headers: {
                  Accept: 'application/vnd.s.v1+json',
                  'Content-Type': 'application/json',
                  'accept-language': storeRoot.userLang,
                },
              });
              return response.data;
            } catch (e: any) {
              if (requestOptions.getErrors && e?.response?.data?.errors) {
                return { errors: e?.response?.data?.errors };
              }

              console.error(e);
              return null;
            }
          };

          // Transfer state cant send Promise to browser,
          // thats why we must resolve promise for before transfer
          const response = import.meta.browser ? axiosRequest() : await axiosRequest();

          return response || null;
        } catch (e: any) {
          console.error(e);
        }
      }
      console.error(new Error('SOFTSWISS CONNECTION ERROR'));
      return null;
    },
  },
});

const _saveToLocalStorage = async (params: TransportCachebleDataInterface) => {
  if (import.meta.browser && localStorage) {
    localStorage[params.requestId] = JSON.stringify(await params.response.response);
    localStorage[params.requestId + '_timestamp'] = params.response.requestTimestamp;
  }
};
